// Unified bulk-import mapping for the staged importer (see
// docs/sample-quizzes/IMPORT-TEMPLATE-GUIDE.md). Hybrid model: deck-level
// metadata (deck name, category, tags, disclaimer) comes from PAGE FIELDS that
// mirror the manual deck creator and act as defaults; the CSV carries the
// questions. The minimum CSV is `question,answer` (header optional — a
// 2-column headerless file is detected and read positionally). Optional
// `domain,subject,topic,deck` columns OVERRIDE the page defaults per row, so one
// file can still create multiple decks.
//
// Pure (no DOM, no network). The server (lib/importDeck.js) re-validates.

import { parseRecords } from "./parseCsv.js";

const ALIASES = {
  deck: ["deck", "deckname", "deck name", "deck_name"],
  domain: ["domain"],
  subject: ["subject"],
  topic: ["topic"],
  question: ["question", "q", "prompt"],
  answer: ["answer", "correctanswer", "correct_answer", "correct answer", "correct"],
  distractor1: ["distractor1", "wrong1", "d1"],
  distractor2: ["distractor2", "wrong2", "d2"],
  distractor3: ["distractor3", "wrong3", "d3"],
  difficulty: ["difficulty", "diff"],
  type: ["type"],
  tags: ["tags"],
  explanation: ["explanation", "feedback", "rationale"],
  memoryHint: ["memory_hint", "memoryhint", "memory hint", "hint", "mnemonic"],
  citation: ["citation", "reference", "ref"],
  description: ["description", "desc", "summary"],
  byline: ["byline", "author", "attribution"]
};
const KNOWN = new Set(Object.values(ALIASES).flat());
// Positional schema for a headerless file (e.g. a bare `question,answer` export).
const POSITIONAL = ["question", "answer", "distractor1", "distractor2", "distractor3", "difficulty", "type"];

// Canonical column order for the downloadable template + the on-page column
// reference. The served file public/templates/import-template.csv MUST use
// exactly this header row (asserted by importTemplate.test.js); the import view
// imports this so the displayed reference can never drift from the template.
export const TEMPLATE_COLUMNS = ["domain", "subject", "topic", "deck", "question", "answer", "distractor1", "distractor2", "distractor3", "difficulty", "type", "tags", "explanation", "memory_hint", "citation", "description", "byline"];

function pick(row, aliases) {
  for (const a of aliases) if (a in row && row[a] != null && String(row[a]).trim() !== "") return String(row[a]).trim();
  return "";
}
const norm = (s) => String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ").replace(/[.,;:!?]+$/, "");

export function distractorState(distractors) {
  const filled = distractors.filter((d) => (d || "").trim() !== "").length;
  if (filled === 0) return "generate";
  if (filled === 3) return "provided";
  return "partial";
}

// Recompute an item's `problems` + `state` from its own (possibly edited) fields.
export function validateMappedItem(item) {
  const problems = [];
  if (!item.question || !item.question.trim()) problems.push("missing question");
  if (!item.answer || !item.answer.trim()) problems.push("missing answer (the correct option)");

  item.state = distractorState(item.distractors);
  if (item.state === "partial") problems.push("fill all three distractors or clear them all (to auto-generate)");
  if (item.state === "provided") {
    const all = [item.answer, ...item.distractors].map(norm);
    if (item.answer && item.distractors.some((d) => norm(d) === norm(item.answer))) problems.push("a distractor equals the answer");
    if (new Set(all).size !== all.length) problems.push("duplicate options");
  }
  if (item.difficulty && !/^[1-5]$/.test(String(item.difficulty).trim())) problems.push(`difficulty "${item.difficulty}" must be 1–5`);
  item.problems = problems;
  return item;
}

function buildItem(row, index) {
  const item = {
    line: index + 1,
    deck: pick(row, ALIASES.deck),
    domain: pick(row, ALIASES.domain),
    subject: pick(row, ALIASES.subject),
    topic: pick(row, ALIASES.topic),
    question: pick(row, ALIASES.question),
    answer: pick(row, ALIASES.answer),
    distractors: [pick(row, ALIASES.distractor1), pick(row, ALIASES.distractor2), pick(row, ALIASES.distractor3)],
    difficulty: pick(row, ALIASES.difficulty),
    type: pick(row, ALIASES.type),
    tags: pick(row, ALIASES.tags),
    explanation: pick(row, ALIASES.explanation),
    memoryHint: pick(row, ALIASES.memoryHint),
    citation: pick(row, ALIASES.citation),
    description: pick(row, ALIASES.description),
    byline: pick(row, ALIASES.byline),
    problems: [],
    state: "generate"
  };
  return validateMappedItem(item);
}

// Parse raw CSV text → { items, headers, unknownColumns, headerless }.
// Detects a headerless file (first record has no recognised column name) and
// reads it positionally as question,answer[,distractor1..3,difficulty,type].
export function parseImportCsv(text) {
  const records = parseRecords(text);
  if (records.length === 0) return { items: [], headers: [], unknownColumns: [], headerless: false };

  const firstLower = records[0].map((c) => c.trim().toLowerCase());
  const headerless = !firstLower.some((c) => KNOWN.has(c));
  const headers = headerless ? POSITIONAL.slice(0, Math.max(2, records[0].length)) : records[0].map((h) => h.trim());
  const keys = headers.map((h) => h.toLowerCase());
  const dataRecords = headerless ? records : records.slice(1);
  const unknownColumns = headerless ? [] : headers.filter((h, i) => h.trim() !== "" && !KNOWN.has(keys[i]));

  const items = dataRecords.map((r, idx) => {
    const o = {};
    keys.forEach((k, i) => { o[k] = (r[i] ?? "").trim(); });
    return buildItem(o, idx);
  });
  return { items, headers, unknownColumns, headerless };
}

// Apply page-field defaults to each item (CSV values win). Mutates + returns
// items. `defaults` = { deckName, domain, subject, topic, tags:[], disclaimer }.
export function mergeDefaults(items, defaults = {}) {
  for (const it of items) {
    it.domain = it.domain || defaults.domain || "";
    it.subject = it.subject || defaults.subject || "";
    it.topic = it.topic || defaults.topic || "";
    it.deck = it.deck || defaults.deckName || it.topic || it.subject || it.domain || "Imported deck";
    it.deckTags = Array.isArray(defaults.tags) ? defaults.tags : [];
    it.disclaimer = defaults.disclaimer || "";
    it.description = it.description || defaults.description || "";
    it.byline = it.byline || defaults.byline || "";
  }
  return items;
}

// Deck grouping key — by deck name within its taxonomy path.
export function deckKey(item) {
  return [item.deck, item.domain, item.subject, item.topic].map(norm).join(" | ");
}

export function groupDecks(items) {
  const byKey = new Map();
  for (const it of items) {
    const key = deckKey(it);
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        name: it.deck || it.topic || it.subject || it.domain || "Imported deck",
        domain: it.domain || null, subject: it.subject || null, topic: it.topic || null,
        deckTags: it.deckTags || [], disclaimer: it.disclaimer || "",
        items: [], generateCount: 0
      });
    }
    const deck = byKey.get(key);
    deck.items.push(it);
    if (it.state === "generate") deck.generateCount++;
  }
  return [...byKey.values()];
}

export function indexExistingNodes(tree) {
  const domains = new Set();
  const subjects = new Map();
  const topics = new Map();
  for (const d of tree || []) {
    domains.add(norm(d.name));
    const subSet = new Set();
    subjects.set(norm(d.name), subSet);
    for (const s of d.children || []) {
      subSet.add(norm(s.name));
      const topSet = new Set();
      topics.set(`${norm(d.name)}|${norm(s.name)}`, topSet);
      for (const t of s.children || []) topSet.add(norm(t.name));
    }
  }
  return { domains, subjects, topics };
}

// Summarise for the confirm screen. Items should already have defaults merged.
export function summariseImport(items, existingTree = []) {
  const idx = indexExistingNodes(existingTree);
  const decks = groupDecks(items);
  const problemRows = items.filter((i) => i.problems.length > 0).length;
  const generationCount = items.filter((i) => i.state === "generate").length;

  const newNodes = [];
  const seen = new Set();
  for (const it of items) {
    if (it.domain) {
      const k = `d:${norm(it.domain)}`;
      if (!seen.has(k)) { seen.add(k); if (!idx.domains.has(norm(it.domain))) newNodes.push({ level: "domain", name: it.domain }); }
    }
    if (it.domain && it.subject) {
      const k = `s:${norm(it.domain)}|${norm(it.subject)}`;
      if (!seen.has(k)) { seen.add(k); if (!idx.subjects.get(norm(it.domain))?.has(norm(it.subject))) newNodes.push({ level: "subject", name: it.subject, under: it.domain }); }
    }
    if (it.domain && it.subject && it.topic) {
      const k = `t:${norm(it.domain)}|${norm(it.subject)}|${norm(it.topic)}`;
      if (!seen.has(k)) { seen.add(k); if (!idx.topics.get(`${norm(it.domain)}|${norm(it.subject)}`)?.has(norm(it.topic))) newNodes.push({ level: "topic", name: it.topic, under: `${it.domain} / ${it.subject}` }); }
    }
  }

  const domainNames = new Set(items.map((i) => norm(i.domain)).filter(Boolean));
  return {
    totalRows: items.length,
    problemRows,
    validRows: items.length - problemRows,
    deckCount: decks.length,
    domainCount: domainNames.size,
    generationCount,
    newNodeCount: newNodes.length,
    newNodes,
    decks,
    blocking: problemRows > 0 || items.length === 0
  };
}
